import { useEffect, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';

const ENABLED_VALUES = ['1', 'true', 'yes', 'y', 'on'];

export const MANUAL_PAYMENT_MEMO_ENABLED = ENABLED_VALUES.includes(
  String(process.env.REACT_APP_MANUAL_PAYMENT_MEMO_ENABLED || '').trim().toLowerCase(),
);

function readMemos(storageKey) {
  if (!storageKey) return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((memo) => ({ content: String(memo?.content || '').trim(), createdAt: memo?.createdAt || null }))
      .filter((memo) => memo.content);
  } catch (_) {
    return [];
  }
}

export default function PaymentMemoPanel({ customerKey, customerName = '', appearance = 'card' }) {
  const normalizedKey = String(customerKey || '').trim();
  const storageKey = normalizedKey ? `paymentMemos_${normalizedKey}` : '';
  const [memos, setMemos] = useState([]);
  const [content, setContent] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  useEffect(() => {
    setMemos(readMemos(storageKey));
    setContent('');
    setEditorOpen(false);
    setDeleteIndex(null);
  }, [storageKey]);

  if (!MANUAL_PAYMENT_MEMO_ENABLED) return null;

  const persist = (nextMemos) => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(nextMemos));
      setMemos(nextMemos);
    } catch (_) {
      // 브라우저 저장소가 제한된 경우 기존 목록을 유지한다.
    }
  };

  const saveMemo = () => {
    const nextContent = content.trim();
    if (!storageKey || !nextContent) return;
    persist([...memos, { content: nextContent, createdAt: new Date().toISOString() }]);
    setContent('');
    setEditorOpen(false);
  };

  const deleteMemo = () => {
    if (deleteIndex === null) return;
    persist(memos.filter((_, index) => index !== deleteIndex));
    setDeleteIndex(null);
  };

  return (
    <>
      <section
        aria-label="거래처 메모"
        className={appearance === 'flat'
          ? 'mt-1 border-t border-[#f0f0f0] px-1 pt-3.5'
          : 'mt-4 rounded-xl border border-[#e5e8eb] bg-white p-4 shadow-[0_4px_14px_rgba(0,0,0,0.06)]'}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <strong className="block text-[16px] leading-5 text-[#191f28]">메모</strong>
            {customerName && <span className="mt-0.5 block truncate text-[11px] text-[#8b95a1]">{customerName}</span>}
          </div>
          <button
            type="button"
            onClick={() => { setContent(''); setEditorOpen(true); }}
            disabled={!storageKey}
            aria-label="메모 추가"
            title={storageKey ? '메모 추가' : '거래처를 먼저 선택해 주세요.'}
            className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg border-0 bg-[var(--brand-primary-soft)] text-[var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus size={18} aria-hidden="true" />
          </button>
        </div>

        {!storageKey ? (
          <p className="mb-0 mt-2.5 text-[13px] text-[#8b95a1]">거래처를 선택하면 메모를 작성할 수 있습니다.</p>
        ) : memos.length === 0 ? (
          <p className="mb-0 mt-2.5 text-[13px] text-[#8b95a1]">저장된 메모가 없습니다.</p>
        ) : (
          <div className="mt-2 flex flex-col">
            {memos.map((memo, index) => (
              <div key={`${memo.createdAt || 'memo'}-${index}`} className="flex items-start gap-2 border-t border-[#f2f4f6] py-2.5">
                <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[14px] leading-[1.5] text-[#333d4b]">{memo.content}</span>
                <button
                  type="button"
                  onClick={() => setDeleteIndex(index)}
                  aria-label={`메모 ${index + 1} 삭제`}
                  className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg border-0 bg-red-50 text-red-500"
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {editorOpen && (
        <MemoOverlay onClose={() => setEditorOpen(false)} labelledBy="payment-memo-title">
          <div className="flex items-center justify-between gap-3">
            <strong id="payment-memo-title" className="text-[18px] text-[#191f28]">메모 추가</strong>
            <button type="button" onClick={() => setEditorOpen(false)} aria-label="닫기" className="flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-[#f2f4f6] text-[#4e5968]">
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <textarea
            autoFocus
            value={content}
            onChange={(event) => setContent(event.target.value.slice(0, 500))}
            placeholder="메모 내용을 입력하세요."
            maxLength={500}
            className="mt-3.5 min-h-[130px] w-full resize-y rounded-[10px] border border-[#dfe3e8] p-3 text-[15px] leading-6 text-[#191f28] outline-none"
          />
          <div className="mt-1 text-right text-[11px] text-[#8b95a1]">{content.length}/500</div>
          <ModalActions>
            <button type="button" onClick={() => setEditorOpen(false)} className={secondaryButtonClass}>취소</button>
            <button type="button" onClick={saveMemo} disabled={!content.trim()} className={`${primaryButtonClass} disabled:cursor-not-allowed disabled:opacity-40`}>저장</button>
          </ModalActions>
        </MemoOverlay>
      )}

      {deleteIndex !== null && (
        <MemoOverlay onClose={() => setDeleteIndex(null)} labelledBy="payment-memo-delete-title" compact>
          <strong id="payment-memo-delete-title" className="text-[18px] text-[#191f28]">메모를 삭제할까요?</strong>
          <p className="mb-0 mt-2 text-[14px] text-[#6b7684]">삭제한 메모는 복구할 수 없습니다.</p>
          <ModalActions>
            <button type="button" onClick={() => setDeleteIndex(null)} className={secondaryButtonClass}>취소</button>
            <button type="button" onClick={deleteMemo} className={`${primaryButtonClass} bg-[#e5484d]`}>삭제</button>
          </ModalActions>
        </MemoOverlay>
      )}
    </>
  );
}

function MemoOverlay({ children, onClose, labelledBy, compact = false }) {
  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-slate-900/50 p-5" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`w-full ${compact ? 'max-w-[360px]' : 'max-w-[420px]'} rounded-[18px] bg-white p-5 shadow-2xl`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalActions({ children }) {
  return <div className="mt-4 flex justify-end gap-2">{children}</div>;
}

const secondaryButtonClass = 'min-w-[82px] rounded-[10px] border-0 bg-[#f2f4f6] px-4 py-2.5 text-[14px] font-bold text-[#333d4b]';
const primaryButtonClass = 'min-w-[82px] rounded-[10px] border-0 bg-[var(--brand-primary)] px-4 py-2.5 text-[14px] font-bold text-white';
